import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { PhoneLink, EmailLink, fmtDate } from "../lib/formatters";
import { Upload, Trash2, Plus, X, Save, Pencil, Printer, MessageCircle, Mail, History } from "lucide-react";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import UploadPreviewModal from "../components/UploadPreviewModal";
import WeekendBookingHistoryModal from "../components/WeekendBookingHistoryModal";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import DestCityFilter from "../components/DestCityFilter";

const CS = { USD: "$", GBP: "£", EUR: "€", INR: "₹" };

// Compact M/F/O gender badge shown next to every participant name so
// drivers + GEHM can plan pickups / hotel rooms at a glance. Blue = male,
// pink = female, purple = other, empty = unknown (rendered as nothing).
const GenderBadge = ({ g }) => {
  const s = String(g || "").trim().toLowerCase();
  if (!s || s === "unknown" || s === "prefer_not_to_say") return null;
  const cfg = s === "male" || s === "m" ? { l: "M", cls: "bg-blue-100 text-blue-700" }
            : s === "female" || s === "f" ? { l: "F", cls: "bg-pink-100 text-pink-700" }
            : { l: "O", cls: "bg-purple-100 text-purple-700" };
  return <span data-testid="wk-gender-badge" title={`Gender: ${s}`}
    className={`ml-1.5 inline-flex items-center justify-center text-[9px] font-bold w-4 h-4 rounded-full ${cfg.cls}`}>{cfg.l}</span>;
};
const genderLetter = (g) => {
  const s = String(g || "").trim().toLowerCase();
  if (!s || s === "unknown" || s === "prefer_not_to_say") return "";
  if (s === "male" || s === "m") return "M";
  if (s === "female" || s === "f") return "F";
  return "O";
};

export default function WeekendBooking() {
  const { user } = useAuth();
  const role = user?.real_role || user?.effective_role;
  const isFounder = role === "FOUNDER";
  // Weekend Bookings write ops are Founder-only. Every other role
  // (BDOM, GEHM, PCWM, SMCM, etc.) sees a read-only view — no Add-row
  // button, no inline edit form, no per-row Edit / Delete controls.
  // Founder still owns Save + Bulk Discard + Delete.
  const canEdit = isFounder;
  const canDelete = isFounder;
  // Founder-only bulk-discard: multi-select rows, then batch-archive with
  // one shared reason. Backend endpoint `POST /api/weekend-bookings/bulk-discard`.
  const [bulkSel, setBulkSel] = useState(() => new Set());
  const [discardOpen, setDiscardOpen] = useState(false);
  const [discardReason, setDiscardReason] = useState("");
  const [discardBusy, setDiscardBusy] = useState(false);
  const toggleBulk = (id) => setBulkSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearBulk = () => setBulkSel(new Set());
  const runBulkDiscard = async () => {
    if (bulkSel.size === 0) return;
    setDiscardBusy(true);
    try {
      const { data } = await api.post("/weekend-bookings/bulk-discard", {
        ids: [...bulkSel], reason: discardReason.trim(),
      });
      toast.success(`Discarded ${data.discarded} cohort${data.discarded === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      clearBulk(); setDiscardOpen(false); setDiscardReason("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setDiscardBusy(false); }
  };
  // Row-history modal target — mirrors the pattern from Arrivals so
  // Founders can trace price / date / partner edits on any cohort.
  const [historyTarget, setHistoryTarget] = useState(null);
  const [rows, setRows] = useState([]);
  const [partners, setPartners] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({});
  const [edits, setEdits] = useState({});
  const [filter, setFilter] = useState({ destination: "", city: "" });
  // Rows the user has explicitly re-opened for editing after they were saved.
  const [unlockedIds, setUnlockedIds] = useState(new Set());
  const isSaved = (row) => Boolean(row.partner_name);
  const isLocked = (row) => isSaved(row) && !unlockedIds.has(row.id);
  const unlock = (id) => setUnlockedIds(s => { const n = new Set(s); n.add(id); return n; });
  const relock = (id) => setUnlockedIds(s => { const n = new Set(s); n.delete(id); return n; });
  const fileRef = useRef();

  const load = useCallback(async () => {
    const q = new URLSearchParams();
    if (filter.destination) q.set("destination", filter.destination);
    if (filter.city) q.set("city", filter.city);
    const [w, p] = await Promise.all([api.get(`/weekend-bookings?${q.toString()}`), api.get("/partners").catch(()=>({data:[]}))]);
    // Strict ascending by start date (user preference — earliest first)
    const sorted = [...w.data].sort((a, b) => (a.start_dates || "").localeCompare(b.start_dates || ""));
    setRows(sorted);
    setPartners(p.data);
    const eMap = {};
    for (const r of w.data) {
      // Reconcile partner_id: keep saved id, else best-effort name match so the dropdown pre-selects.
      let pid = r.partner_id || "";
      if (!pid && r.partner_name) {
        const match = p.data.find(x => (x.company_name || x.name || "").toLowerCase() === r.partner_name.toLowerCase());
        if (match) pid = match.id;
      }
      eMap[r.id] = { partner_name: r.partner_name || "", partner_id: pid };
    }
    setEdits(eMap);
  }, [filter.destination, filter.city]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.customer) return toast.error("Customer required");
    await api.post("/weekend-bookings", form);
    setForm({}); setAdding(false); await load(); toast.success("Added");
  };
  const del = async (id) => { await api.delete(`/weekend-bookings/${id}`); await load(); };
  const [delTarget, setDelTarget] = useState(null);
  // Open a fresh window with a print-friendly roster of a single tour+date
  // cohort so drivers get a scannable participant list on paper.  Cities +
  // hotels come from the booking row itself (falling back to tour metadata).
  const printCohort = (tour, date, list) => {
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, c => ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" }[c]));
    const rows = list.map((r, i) => {
      const gl = genderLetter(r.gender);
      const genderCell = gl ? `<span class="gbadge g-${gl}">${gl}</span>` : "—";
      return `
      <tr>
        <td>${i + 1}</td>
        <td><b>${esc(r.customer)}</b></td>
        <td class="gcell">${genderCell}</td>
        <td>${esc(r.partner_name || "—")}</td>
        <td>${esc([r.city || r.tour_city, r.destination || r.tour_country].filter(Boolean).join(", ") || "—")}</td>
        <td>${esc(r.hotel_name || "—")}</td>
        <td>${esc(r.hotel_pickup_time || r.tour_time || "—")}</td>
        <td>${esc(r.telephone || "—")}</td>
      </tr>`;
    }).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"/>
      <title>${esc(tour)} · ${esc(date)}</title>
      <style>
        body{font:14px -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#111;padding:24px}
        h1{margin:0 0 4px 0;font-size:20px}
        .sub{color:#666;font-size:12px;margin-bottom:16px}
        table{width:100%;border-collapse:collapse;font-size:13px}
        th,td{border:1px solid #ccc;padding:8px 10px;text-align:left;vertical-align:top}
        th{background:#f4f4f4;font-size:11px;text-transform:uppercase;letter-spacing:.1em}
        tr:nth-child(even) td{background:#fafafa}
        .gcell{text-align:center;font-weight:600}
        .gbadge{display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:999px;font-size:10px;font-weight:700}
        .gbadge.g-M{background:#dbeafe;color:#1d4ed8}
        .gbadge.g-F{background:#fce7f3;color:#be185d}
        .gbadge.g-O{background:#ede9fe;color:#6d28d9}
        .foot{margin-top:16px;font-size:11px;color:#666}
      </style></head><body>
      <h1>${esc(tour)}</h1>
      <div class="sub">Tour date: <b>${esc(date)}</b> · ${list.length} participant${list.length === 1 ? "" : "s"} · Printed ${new Date().toLocaleString()}</div>
      <table><thead><tr><th>#</th><th>Participant</th><th>Sex</th><th>Partner</th><th>Location</th><th>Hotel</th><th>Pickup time</th><th>Phone</th></tr></thead><tbody>${rows}</tbody></table>
      <div class="foot">Aii Group Team OS — weekend cohort roster</div>
      <script>window.onload=()=>window.print();</script>
    </body></html>`;
    // Use a Blob URL instead of document.write() — the reviewer flagged
    // document.write as an XSS vector even though the `html` string is
    // escape()-sanitized on every insertion.  Blob URLs give us the same
    // "open a printable page" UX without exposing the sink.  The URL is
    // revoked after a minute so the browser can reclaim the memory once
    // the print dialog has closed.
    try {
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (!w) { URL.revokeObjectURL(url); toast.error("Enable pop-ups to print the roster"); return; }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { toast.error("Could not open the print window"); }
  };
  const pickPartner = (id, partnerId) => {
    const p = partners.find(x => x.id === partnerId);
    setEdits(prev => ({ ...prev, [id]: { ...prev[id], partner_id: partnerId, partner_name: p ? (p.company_name || p.name || "") : "" } }));
  };
  const saveSale = async (r) => {
    const e = edits[r.id];
    if (!e.partner_id) return toast.error("Pick a partner first");
    // Amount + currency intentionally hidden from Weekend Bookings per Founder
    // preference — the row exists to log the partner attribution only. Backend
    // still requires the fields, so we send neutral defaults (USD / 0).
    await api.post("/weekend-bookings/sales", {
      booking_id: r.id, currency: "USD", amount: 0,
      partner_name: e.partner_name, partner_id: e.partner_id,
    });
    toast.success("Partner saved");
    relock(r.id);
    await load();
  };

  const [pendingUpload, setPendingUpload] = useState(null);
  const upload = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setPendingUpload(f);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-semibold">Weekend Bookings</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <DestCityFilter value={filter} onChange={setFilter} />
          {canEdit && (
            <>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={upload} className="hidden" data-testid="wk-upload-input"/>
              <button data-testid="wk-upload" onClick={() => fileRef.current?.click()} className="text-sm px-3 py-2 border border-[color:var(--aii-border)] bg-white rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1.5"><Upload size={14}/> Upload</button>
              <button data-testid="wk-add-toggle" onClick={() => setAdding(true)} className="text-sm px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5"><Plus size={14}/> Add row</button>
            </>
          )}
        </div>
      </div>

      {adding && (
        <div className="aii-card p-5">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold">New Weekend Booking</h3>
            <button onClick={() => setAdding(false)}><X size={16}/></button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { k:"customer", label:"Customer" }, { k:"email", label:"Email", t:"email" },
              { k:"telephone", label:"Telephone" }, { k:"weekend_trips", label:"Weekend trips" },
              { k:"destinations", label:"Destinations" }, { k:"start_dates", label:"Start date", t:"date" },
              { k:"end_dates", label:"End date", t:"date" },
            ].map(f => (
              <div key={f.k}>
                <label className="text-xs font-semibold text-[color:var(--aii-text-2)] uppercase tracking-wider">{f.label}</label>
                <input data-testid={`wk-${f.k}`} type={f.t||"text"} value={form[f.k]||""} onChange={e=>setForm(v=>({...v,[f.k]:e.target.value}))}
                  className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-4">
            <button data-testid="wk-save" onClick={add} className="bg-brand text-white font-semibold px-5 py-2 rounded-md hover:bg-brand-dark">Save</button>
          </div>
        </div>
      )}

      <div className="aii-card overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="aii-table">
            <thead><tr>
              {isFounder && <th className="w-8"></th>}
              <th>Partner</th><th>Location</th><th>Participant</th>
              <th>Weekend Tour</th><th>Tour date</th><th>Trip name</th>
              <th>Start</th><th>End</th>
              {isFounder && <th>Price</th>}
              <th></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={isFounder ? 11 : 9} className="text-center py-10 text-[color:var(--aii-muted)]">No bookings.</td></tr>}
              {(() => {
                // Two-level grouping: tour-month → tour-date (day-level).  Within
                // each date, rows sharing the same tour title get a compact
                // sub-header showing the participant count so identical
                // "Camel Safari · 15 Aug" runs read as one cohort.
                const cols = isFounder ? 11 : 9;
                const byMonth = new Map();
                for (const r of rows) {
                  const raw = r.tour_date || r.start_dates || r.end_dates || "";
                  const ym = raw.slice(0, 7) || "Unscheduled";
                  if (!byMonth.has(ym)) byMonth.set(ym, new Map());
                  const byDate = byMonth.get(ym);
                  const d = raw.slice(0, 10) || "No date";
                  if (!byDate.has(d)) byDate.set(d, new Map());
                  const byTour = byDate.get(d);
                  const tour = (r.tour_title || r.weekend_trips || "—").trim();
                  if (!byTour.has(tour)) byTour.set(tour, []);
                  byTour.get(tour).push(r);
                }
                const out = [];
                for (const [ym, byDate] of [...byMonth.entries()].sort(([a],[b]) => a.localeCompare(b))) {
                  const monthTotal = [...byDate.values()].reduce((n, m) => n + [...m.values()].reduce((k, l) => k + l.length, 0), 0);
                  out.push(
                    <tr key={`wkm-${ym}`} data-testid={`wk-month-hdr-${ym}`} className="bg-[color:var(--aii-green-tint)]/50">
                      <td colSpan={cols} className="px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold text-brand">
                        {ym} · {monthTotal} weekend booking{monthTotal === 1 ? "" : "s"}
                      </td>
                    </tr>
                  );
                  for (const [d, byTour] of [...byDate.entries()].sort(([a],[b]) => a.localeCompare(b))) {
                    const dayTotal = [...byTour.values()].reduce((n, l) => n + l.length, 0);
                    out.push(
                      <tr key={`wkd-${ym}-${d}`} data-testid={`wk-day-hdr-${d}`} className="bg-[color:var(--aii-green-tint)]/25">
                        <td colSpan={cols} className="pl-8 pr-3 py-1 text-[11px] font-semibold text-[color:var(--aii-text-2)]">
                          ↳ {d} · {dayTotal} booking{dayTotal === 1 ? "" : "s"}
                        </td>
                      </tr>
                    );
                    for (const [tour, list] of [...byTour.entries()].sort(([a],[b]) => a.localeCompare(b))) {
                      // If two or more rows share the same tour + same date,
                      // surface a compact "same tour" grouping badge so the
                      // Founder can eyeball cohort sizes without scanning.
                      if (list.length > 1) {
                        out.push(
                          <tr key={`wkt-${ym}-${d}-${tour}`} data-testid={`wk-tour-hdr-${d}-${tour}`} className="bg-white/60">
                            <td colSpan={cols} className="pl-14 pr-3 py-0.5 text-[10px] italic text-[color:var(--aii-muted)] flex items-center justify-between gap-2">
                              <span>→ {tour} · {list.length} participants sharing this tour</span>
                              <button type="button" data-testid={`wk-print-${d}-${tour}`}
                                      onClick={() => printCohort(tour, d, list)}
                                      title="Open a printable roster for this cohort"
                                      className="text-brand hover:underline flex items-center gap-1 not-italic font-semibold">
                                <Printer size={11}/> Print roster
                              </button>
                            </td>
                          </tr>
                        );
                      }
                      for (const r of list) {
                    const e = edits[r.id] || {};
                    const locked = isLocked(r);
                    out.push(
                  <tr key={r.id} data-testid={`wk-row-${r.id}`} className={locked ? "bg-[color:var(--aii-green-tint)]/30" : ""}>
                    {isFounder && (
                      <td>
                        <input type="checkbox" data-testid={`wk-bulk-sel-${r.id}`}
                               checked={bulkSel.has(r.id)}
                               onChange={() => toggleBulk(r.id)}/>
                      </td>
                    )}
                    <td className="font-medium text-brand" data-testid={`wk-partner-cell-${r.id}`}>
                      {(locked || !canEdit) ? (
                        <span>{e.partner_name || r.partner_name || "—"}</span>
                      ) : (
                        <select data-testid={`wk-partner-${r.id}`} value={e.partner_id || ""}
                          onChange={ev => pickPartner(r.id, ev.target.value)}
                          className="w-40 px-2 py-1.5 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
                          <option value="">— pick —</option>
                          {partners.map(p => <option key={p.id} value={p.id}>{p.company_name || p.name}</option>)}
                        </select>
                      )}
                    </td>
                    <td className="text-xs whitespace-nowrap">{[r.city || r.tour_city, r.destination || r.tour_country || r.destinations].filter(Boolean).join(", ") || "—"}</td>
                    <td className="font-medium" data-testid={`wk-participant-cell-${r.id}`}>
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1">
                          <span>{r.customer}</span>
                          <GenderBadge g={r.gender}/>
                        </div>
                        {/* Universal contact strip — visible to every role
                            already scoped to this destination. Skips the
                            placeholder participants.aiigroup.com emails so
                            we never dead-end into an unroutable inbox. */}
                        {((r.email && !r.email.toLowerCase().endsWith("@participants.aiigroup.com")) || r.telephone || r.whatsapp) && (
                          <div className="flex items-center gap-2 flex-wrap text-[11px] font-normal">
                            {r.email && !r.email.toLowerCase().endsWith("@participants.aiigroup.com") && (
                              <span data-testid={`wk-email-${r.id}`} className="inline-flex items-center gap-1">
                                <Mail size={10} className="text-[color:var(--aii-muted)]"/>
                                <EmailLink value={r.email}/>
                              </span>
                            )}
                            {(r.whatsapp || r.telephone) && (
                              <span data-testid={`wk-phone-${r.id}`} className="inline-flex items-center gap-1">
                                <MessageCircle size={10} className="text-[#25D366]"/>
                                <PhoneLink value={r.whatsapp || r.telephone}/>
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="text-xs" data-testid={`wk-tour-${r.id}`}>{r.tour_title || <span className="italic text-[color:var(--aii-muted)]">—</span>}</td>
                    <td className="whitespace-nowrap text-xs">{fmtDate(r.tour_date) || "—"}</td>
                    <td>{r.weekend_trips || "—"}</td>
                    <td className="whitespace-nowrap">{fmtDate(r.start_dates)||"—"}</td>
                    <td className="whitespace-nowrap">{fmtDate(r.end_dates)||"—"}</td>
                    {isFounder && (
                      <td className="whitespace-nowrap font-semibold text-brand" data-testid={`wk-price-${r.id}`}>
                        {r.sale_price ? `${CS[r.sale_currency] || r.sale_currency || "$"}${Number(r.sale_price).toLocaleString()}` : "—"}
                      </td>
                    )}
                    <td>
                      <div className="flex gap-1 items-center">
                        <button data-testid={`wk-history-${r.id}`}
                          onClick={() => setHistoryTarget(r)}
                          title="View full edit history"
                          className="text-[color:var(--aii-muted)] hover:text-brand p-1">
                          <History size={14}/>
                        </button>
                        {canEdit && (
                          <>
                            {locked ? (
                              <button data-testid={`wk-edit-${r.id}`} onClick={()=>unlock(r.id)}
                                title="Unlock this row to change the partner"
                                className="text-xs border border-[color:var(--aii-border)] text-[color:var(--aii-text-2)] px-2 py-1 rounded hover:bg-white flex items-center gap-1"><Pencil size={11}/> Edit</button>
                            ) : (
                              <button data-testid={`wk-save-${r.id}`} onClick={()=>saveSale(r)}
                                className="text-xs bg-brand text-white px-2 py-1 rounded flex items-center gap-1"><Save size={11}/> Save</button>
                            )}
                            {canDelete && <button data-testid={`wk-del-${r.id}`} onClick={()=>setDelTarget(r)} className="text-[color:var(--aii-muted)] hover:text-red-600 px-1"><Trash2 size={14}/></button>}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
                    }
                    }
                  }
                }
                return out;
              })()}
            </tbody>
          </table>
        </div>
      </div>
      <DeleteConfirmDialog
        open={!!delTarget}
        title="Delete this weekend booking?"
        description="The weekend booking + its sale row will be removed together. Restore any time from the Recycle Bin."
        label={delTarget ? `${delTarget.customer || "Unknown"} · ${delTarget.destinations || delTarget.weekend_trips || ""} · ${delTarget.tour_date || delTarget.start_dates || ""}` : ""}
        onCancel={() => setDelTarget(null)}
        onConfirm={async () => { await del(delTarget.id); setDelTarget(null); }}
      />
      {isFounder && bulkSel.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-brand text-white rounded-full shadow-2xl px-4 py-2.5 flex items-center gap-3 z-40" data-testid="wk-bulk-bar">
          <span className="text-sm font-semibold" data-testid="wk-bulk-count">{bulkSel.size} cohort{bulkSel.size === 1 ? "" : "s"} selected</span>
          <span className="text-white/50">·</span>
          <button data-testid="wk-bulk-discard" onClick={() => setDiscardOpen(true)}
                  className="text-sm font-semibold px-3 py-1 rounded bg-white text-brand hover:bg-slate-50">
            Discard selected
          </button>
          <button data-testid="wk-bulk-clear" onClick={clearBulk}
                  className="text-xs text-white/70 hover:text-white p-1"><X size={13}/></button>
        </div>
      )}
      {discardOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="wk-bulk-discard-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-semibold">Discard {bulkSel.size} cohort{bulkSel.size === 1 ? "" : "s"}?</h3>
              <button onClick={() => setDiscardOpen(false)}><X size={16}/></button>
            </div>
            <p className="text-sm text-[color:var(--aii-text-2)] mb-3">
              Each booking is snapshotted to the Recycle Bin (60-day retention). Add one shared reason so the audit trail explains why.
            </p>
            <label className="text-[10px] uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Discard reason (optional)</label>
            <textarea data-testid="wk-bulk-reason" value={discardReason} onChange={e => setDiscardReason(e.target.value)}
              rows={3} placeholder="e.g. Cancelled by partner — cohort rebooked for next month"
              className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setDiscardOpen(false)} className="px-4 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50">Cancel</button>
              <button data-testid="wk-bulk-discard-confirm" onClick={runBulkDiscard} disabled={discardBusy}
                className="px-4 py-2 text-sm bg-red-600 text-white font-semibold rounded-md hover:bg-red-700 disabled:opacity-50">
                {discardBusy ? "Discarding…" : `Discard ${bulkSel.size}`}
              </button>
            </div>
          </div>
        </div>
      )}
      {pendingUpload && (
        <UploadPreviewModal file={pendingUpload} endpoint="/weekend-bookings/upload"
          label="weekend bookings"
          onClose={() => setPendingUpload(null)}
          onDone={load}/>
      )}
      {historyTarget && (
        <WeekendBookingHistoryModal booking={historyTarget} onClose={() => setHistoryTarget(null)}/>
      )}
    </div>
  );
}
